import { gql } from '@apollo/client';
import type { PageableContent } from '@cobliteam/common-types';
import type {
  AudioFileResponse,
  CreateFleetAudioMessageInput,
  FleetAudioMessage,
  FleetAudioMessagePlayback,
  PlayFleetAudioMessageInput,
  QueryFleetAudioMessagePlaybacksArgs,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';

// Re-export GraphQL-generated types for external use
export type { AudioFileResponse, CreateFleetAudioMessageInput, FleetAudioMessage, FleetAudioMessagePlayback, PlayFleetAudioMessageInput };

// Aliases for backward compatibility
export type FleetAudioMessageResponse = FleetAudioMessage;
export type FleetAudioMessagePlaybackResponse = FleetAudioMessagePlayback;
export type GetFleetAudioMessagePlaybacksInput = QueryFleetAudioMessagePlaybacksArgs;

export class GraphQLFleetAudioRequester {
  private createFleetAudioMessageMutation = gql`
    mutation createFleetAudioMessage($input: CreateFleetAudioMessageInput!) {
      createFleetAudioMessage(input: $input) {
        id
        fleetId
        name
        message
        mp3Url
        createdBy {
          id
          name
        }
        createdAt
      }
    }
  `;

  private playFleetAudioMessageMutation = gql`
    mutation playFleetAudioMessage($audioMessageId: ID!, $input: PlayFleetAudioMessageInput!) {
      playFleetAudioMessage(audioMessageId: $audioMessageId, input: $input) {
        id
        audioMessageId
        message
        vehicle {
          id
          licensePlate
        }
        device {
          id
        }
        status
        driver {
          id
          name
        }
        retryCount
        playedAt
        createdAt
        createdBy {
          id
          name
        }
      }
    }
  `;

  private getFleetAudioMessagePlaybacksQuery = gql`
    query getFleetAudioMessagePlaybacks($vehicleId: ID!, $page: Int, $size: Int) {
      fleetAudioMessagePlaybacks(vehicleId: $vehicleId, page: $page, size: $size) {
        content {
          id
          audioMessageId
          message
          vehicle {
            id
            licensePlate
          }
          device {
            id
          }
          status
          driver {
            id
            name
          }
          retryCount
          playedAt
          createdAt
          createdBy {
            id
            name
          }
          cancelIfTimeout
        }
        first
        last
        number
        numberOfElements
        size
        totalElements
        totalPages
      }
    }
  `;

  private retryFleetAudioMessagePlaybackMutation = gql`
    mutation retryFleetAudioMessagePlayback($audioMessageId: ID!, $playbackId: ID!) {
      retryFleetAudioMessagePlayback(audioMessageId: $audioMessageId, playbackId: $playbackId) {
        id
        audioMessageId
        message
        vehicle {
          id
          licensePlate
        }
        device {
          id
        }
        status
        driver {
          id
          name
        }
        retryCount
        playedAt
        createdAt
        createdBy {
          id
          name
        }
        cancelIfTimeout
      }
    }
  `;

  private deleteFleetAudioMessagePlaybackMutation = gql`
    mutation deleteFleetAudioMessagePlayback($audioMessageId: ID!, $playbackId: ID!) {
      deleteFleetAudioMessagePlayback(audioMessageId: $audioMessageId, playbackId: $playbackId)
    }
  `;

  private getAudioFileQuery = gql`
    query getAudioFile($playbackId: ID!) {
      getAudioFile(playbackId: $playbackId) {
        audioUrl
      }
    }
  `;

  public createFleetAudioMessage = (input: CreateFleetAudioMessageInput): Promise<FleetAudioMessageResponse> => {
    return graphQLRequester
      .mutate<{ createFleetAudioMessage: FleetAudioMessageResponse }>({
        mutation: this.createFleetAudioMessageMutation,
        variables: { input },
      })
      .then((result) => result.createFleetAudioMessage);
  };

  public playFleetAudioMessage = (audioMessageId: string, input: PlayFleetAudioMessageInput): Promise<FleetAudioMessagePlaybackResponse> => {
    return graphQLRequester
      .mutate<{ playFleetAudioMessage: FleetAudioMessagePlaybackResponse }>({
        mutation: this.playFleetAudioMessageMutation,
        variables: { audioMessageId, input },
      })
      .then((result) => result.playFleetAudioMessage);
  };

  public getFleetAudioMessagePlaybacks = (input: GetFleetAudioMessagePlaybacksInput): Promise<PageableContent<FleetAudioMessagePlaybackResponse>> => {
    return graphQLRequester
      .query<{ fleetAudioMessagePlaybacks: PageableContent<FleetAudioMessagePlaybackResponse> }>({
        query: this.getFleetAudioMessagePlaybacksQuery,
        variables: {
          vehicleId: input.vehicleId,
          page: input.page ?? 0,
          size: input.size ?? 10,
        },
      })
      .then((result) => result.fleetAudioMessagePlaybacks);
  };

  public retryFleetAudioMessagePlayback = (audioMessageId: string, playbackId: string): Promise<FleetAudioMessagePlaybackResponse | null> => {
    return graphQLRequester
      .mutate<{ retryFleetAudioMessagePlayback: FleetAudioMessagePlaybackResponse | null }>({
        mutation: this.retryFleetAudioMessagePlaybackMutation,
        variables: { audioMessageId, playbackId },
      })
      .then((result) => result.retryFleetAudioMessagePlayback);
  };

  public deleteFleetAudioMessagePlayback = (audioMessageId: string, playbackId: string): Promise<boolean> => {
    return graphQLRequester
      .mutate<{ deleteFleetAudioMessagePlayback: boolean }>({
        mutation: this.deleteFleetAudioMessagePlaybackMutation,
        variables: { audioMessageId, playbackId },
      })
      .then((result) => result.deleteFleetAudioMessagePlayback);
  };

  public getAudioFile = (playbackId: string): Promise<AudioFileResponse> => {
    return graphQLRequester
      .query<{ getAudioFile: AudioFileResponse }>({
        query: this.getAudioFileQuery,
        variables: { playbackId },
      })
      .then((result) => result.getAudioFile);
  };
}

export const getGraphQLFleetAudioRequester = (): GraphQLFleetAudioRequester => {
  return new GraphQLFleetAudioRequester();
};
